import { zodResolver } from '@hookform/resolvers/zod'
import type {
  MfaEnrollmentCompletedResponseDto,
  MfaEnrollmentResponseDto,
  StaffLoginDto,
  StaffSessionTokensResponseDto
} from '@idc-forms/api-client'
import { KeyRound, Loader2, Smartphone } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link, Navigate, useLocation, useNavigate } from 'react-router'
import { z } from 'zod'
import { PATHS } from '@/app/router/routes'
import { MfaEnrollment } from '@/features/account/api-tokens/components/mfa-enrollment'
import { MfaRecoveryCodesDialog } from '@/features/account/api-tokens/components/mfa-recovery-codes-dialog'
import { Button } from '@/shared/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/shared/ui/card'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/shared/ui/input-otp'
import { PasswordInput } from '@/shared/ui/password-input'
import { extractApiError, handleRetryableMutationError } from '@/shared/utils/handle-api-error'
import LayoutAuth from '../components/layout-auth'
import useAuth from '../hooks/use-auth'
import { staffSessionV2Service } from '../staff-session-v2.service'

const credentialsSchema = z.object({
  username: z.string().trim().min(1, 'Ingresa tu usuario.'),
  password: z.string().min(1, 'Ingresa tu contraseña.')
})

type CredentialsFormValues = z.infer<typeof credentialsSchema>
type MfaMethod = 'totp' | 'recovery'

const otpSlotClassName = 'h-11 w-8 min-[360px]:h-12 min-[360px]:w-9 sm:h-14 sm:w-14'

type CredentialsFormProps = {
  pending: boolean
  onSubmit: (values: CredentialsFormValues) => Promise<void>
}

function CredentialsForm({ pending, onSubmit }: CredentialsFormProps) {
  const { control, handleSubmit } = useForm<CredentialsFormValues>({
    defaultValues: { username: '', password: '' },
    resolver: zodResolver(credentialsSchema)
  })

  return (
    <form className="grid w-full grid-cols-1 gap-6" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="username"
        render={({ field, fieldState }) => (
          <Field data-invalid={Boolean(fieldState.error)}>
            <FieldLabel htmlFor="staff-login-username">Usuario</FieldLabel>
            <Input
              {...field}
              id="staff-login-username"
              autoCapitalize="none"
              autoComplete="username"
              spellCheck={false}
            />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />

      <Controller
        control={control}
        name="password"
        render={({ field, fieldState }) => (
          <Field data-invalid={Boolean(fieldState.error)}>
            <FieldLabel htmlFor="staff-login-password">Contraseña</FieldLabel>
            <PasswordInput {...field} id="staff-login-password" autoComplete="current-password" />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />

      <Button type="submit" disabled={pending}>
        {pending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
        {pending ? 'Ingresando…' : 'Iniciar sesión'}
      </Button>
    </form>
  )
}

type MfaChallengeFormProps = {
  pending: boolean
  onBack: () => void
  onSubmit: (method: MfaMethod, code: string) => Promise<void>
}

function MfaChallengeForm({ pending, onBack, onSubmit }: MfaChallengeFormProps) {
  const [method, setMethod] = useState<MfaMethod>('totp')
  const {
    control,
    handleSubmit,
    resetField,
    formState: { isValid }
  } = useForm<{ code: string }>({
    defaultValues: { code: '' },
    mode: 'onChange'
  })

  const changeMethod = (value: MfaMethod) => {
    setMethod(value)
    resetField('code')
  }

  const isTotp = method === 'totp'

  return (
    <LayoutAuth>
      <Card className="w-full max-w-md overflow-hidden border-none shadow-none bg-transparent">
        <CardHeader className="space-y-2 p-5 sm:p-6">
          <CardTitle className="text-xl">
            {isTotp ? 'Verifica tu acceso' : 'Recupera tu acceso'}
          </CardTitle>
          <CardDescription className="leading-relaxed">
            {isTotp
              ? 'Ingresa el código de seis dígitos que genera tu aplicación de autenticación.'
              : 'Ingresa uno de los códigos que guardaste al activar la autenticación de dos factores.'}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit(async ({ code }) => await onSubmit(method, code))}>
          <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
            <Controller
              control={control}
              name="code"
              rules={{
                required: 'Ingresa el código de autenticación.',
                validate: (value) =>
                  isTotp
                    ? /^\d{6}$/.test(value) || 'El código debe tener seis dígitos.'
                    : /^[A-Fa-f0-9]{6}-[A-Fa-f0-9]{6}$/.test(value.trim()) ||
                      'Usa el formato XXXXXX-XXXXXX.'
              }}
              render={({ field, fieldState }) => (
                <Field className="gap-3" data-invalid={Boolean(fieldState.error)}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <FieldLabel
                      htmlFor={isTotp ? 'staff-login-totp' : 'staff-login-recovery'}
                      className="w-auto text-sm"
                    >
                      <span className="sm:hidden">Código</span>
                      <span className="hidden sm:inline">Código de verificación</span>
                    </FieldLabel>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0 px-2.5 text-xs"
                      onClick={() => changeMethod(isTotp ? 'recovery' : 'totp')}
                      disabled={pending}
                    >
                      {isTotp ? (
                        <KeyRound className="size-3.5" aria-hidden="true" />
                      ) : (
                        <Smartphone className="size-3.5" aria-hidden="true" />
                      )}
                      {isTotp ? 'Recuperación' : 'Autenticador'}
                    </Button>
                  </div>

                  {isTotp ? (
                    <InputOTP
                      id="staff-login-totp"
                      maxLength={6}
                      value={field.value}
                      onChange={field.onChange}
                      containerClassName="mx-auto w-fit gap-1 sm:gap-2 justify-center"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      aria-label="Código de autenticación de seis dígitos"
                      aria-describedby="staff-login-totp-description staff-login-totp-error"
                      aria-invalid={Boolean(fieldState.error)}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot className={otpSlotClassName} index={0} />
                        <InputOTPSlot className={otpSlotClassName} index={1} />
                        <InputOTPSlot className={otpSlotClassName} index={2} />
                      </InputOTPGroup>
                      <span className="px-0.5 text-lg text-muted-foreground" aria-hidden="true">
                        –
                      </span>
                      <InputOTPGroup>
                        <InputOTPSlot className={otpSlotClassName} index={3} />
                        <InputOTPSlot className={otpSlotClassName} index={4} />
                        <InputOTPSlot className={otpSlotClassName} index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  ) : (
                    <Input
                      {...field}
                      id="staff-login-recovery"
                      autoCapitalize="characters"
                      autoComplete="off"
                      maxLength={13}
                      placeholder="ABC123-DEF456"
                      className="font-mono uppercase tracking-wider"
                    />
                  )}

                  <FieldDescription
                    id={isTotp ? 'staff-login-totp-description' : undefined}
                    className={isTotp ? 'text-center' : undefined}
                  >
                    {isTotp
                      ? 'El código cambia automáticamente cada 30 segundos.'
                      : 'Cada código de recuperación funciona una sola vez.'}
                  </FieldDescription>
                  <FieldError
                    id={isTotp ? 'staff-login-totp-error' : undefined}
                    className={isTotp ? 'text-center' : undefined}
                    errors={[fieldState.error]}
                  />
                </Field>
              )}
            />
          </CardContent>

          <CardFooter className="flex-col gap-3 bg-muted/40 p-5 sm:p-6">
            <Button className="w-full" type="submit" disabled={pending || !isValid}>
              {pending ? <Loader2 className="size-4 animate-spin" /> : null}
              {pending ? 'Validando…' : 'Verificar'}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              ¿Necesitas cambiar de usuario?{' '}
              <button
                type="button"
                className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 disabled:pointer-events-none disabled:opacity-50"
                onClick={onBack}
                disabled={pending}
              >
                Regresa al inicio de sesión
              </button>
            </p>
          </CardFooter>
        </form>
      </Card>
    </LayoutAuth>
  )
}

export function LoginV2() {
  const navigate = useNavigate()
  const location = useLocation()
  const { auth, completeV2Session } = useAuth()
  const [pending, setPending] = useState(false)
  const [credentials, setCredentials] = useState<CredentialsFormValues | null>(null)
  const [pendingSession, setPendingSession] = useState<StaffSessionTokensResponseDto | null>(null)
  const [enrollment, setEnrollment] = useState<MfaEnrollmentResponseDto | null>(null)
  const [completedEnrollment, setCompletedEnrollment] =
    useState<MfaEnrollmentCompletedResponseDto | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)

  const from = location.state?.from
  const redirectTo = from
    ? `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`
    : PATHS.admin.root

  const enterApplication = async (session: StaffSessionTokensResponseDto) => {
    setPending(true)
    try {
      await completeV2Session(session)
      navigate(redirectTo, { replace: true })
    } catch (error) {
      handleRetryableMutationError(error, () => void enterApplication(session))
    } finally {
      setPending(false)
    }
  }

  const acceptSession = async (session: StaffSessionTokensResponseDto, password: string) => {
    if (!session.mfa.required || session.mfa.enrolled) {
      await enterApplication(session)
      return
    }

    const nextEnrollment = await staffSessionV2Service.startMfaEnrollment(session.accessToken, {
      password
    })
    setPendingSession(session)
    setEnrollment(nextEnrollment)
  }

  const submitCredentials = async (values: CredentialsFormValues) => {
    const normalized = { username: values.username.trim().toLowerCase(), password: values.password }
    setPending(true)
    try {
      const session = await staffSessionV2Service.login({
        ...normalized,
        deviceName: 'IDC Forms Web'
      })
      await acceptSession(session, normalized.password)
    } catch (error) {
      if (extractApiError(error)?.errorCode === 'MFA_REQUIRED') {
        setCredentials(normalized)
      } else {
        handleRetryableMutationError(error, () => void submitCredentials(values))
      }
    } finally {
      setPending(false)
    }
  }

  const submitMfa = async (method: MfaMethod, code: string) => {
    if (!credentials) return
    setPending(true)
    const normalizedCode = code.trim()
    const body: StaffLoginDto = {
      ...credentials,
      deviceName: 'IDC Forms Web',
      ...(method === 'totp'
        ? { totp: normalizedCode }
        : { recoveryCode: normalizedCode.toUpperCase() })
    }

    try {
      const session = await staffSessionV2Service.login(body)
      await enterApplication(session)
    } catch (error) {
      handleRetryableMutationError(error, () => void submitMfa(method, code))
    } finally {
      setPending(false)
    }
  }

  const completeEnrollment = (result: MfaEnrollmentCompletedResponseDto) => {
    setCredentials(null)
    setCompletedEnrollment(result)
    setRecoveryCodes(result.recoveryCodes)
  }

  const closeRecoveryCodes = () => {
    const session = completedEnrollment
    setRecoveryCodes(null)
    setCompletedEnrollment(null)
    setEnrollment(null)
    setPendingSession(null)
    if (session) void enterApplication(session)
  }

  if (auth?.id) return <Navigate to={redirectTo} replace />

  if (enrollment && pendingSession) {
    return (
      <LayoutAuth
        title="Protege tu cuenta"
        description="Tu organización exige 2FA antes de permitir el acceso al sistema."
      >
        <MfaEnrollment
          accessToken={pendingSession.accessToken}
          enrollment={enrollment}
          description="Esta cuenta debe completar la autenticación de dos factores antes de ingresar. Escanea el QR y confirma el primer código."
          verifyEnrollment={(accessToken, code) =>
            staffSessionV2Service.completeMfaEnrollment(accessToken, { code })
          }
          onCompleted={completeEnrollment}
        />
        <MfaRecoveryCodesDialog codes={recoveryCodes} onClose={closeRecoveryCodes} />
      </LayoutAuth>
    )
  }

  if (credentials) {
    return (
      <MfaChallengeForm
        pending={pending}
        onBack={() => setCredentials(null)}
        onSubmit={submitMfa}
      />
    )
  }

  return (
    <LayoutAuth
      title="¡Bienvenido!"
      description="Inicia sesión en tu cuenta de IDC Forms - Real Estate."
    >
      <CredentialsForm pending={pending} onSubmit={submitCredentials} />

      <Button variant="link" asChild>
        <Link to={PATHS.auth.forgotPassword}>¿Olvidaste tu contraseña?</Link>
      </Button>
    </LayoutAuth>
  )
}
